import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../core/sdk';
import { useEffect } from 'react';
import { Button } from 'primereact/button';
import { formatDate } from '../../../utils/helper';
import { EmployeeOverviewStore, EmployeeDialogStore } from '../';
import { Dropdown } from 'primereact/dropdown';
import { MatenFilter, MatenTable, MatenTableAction, MatenTableColumn } from '../../../shared';

const EmployeeOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(EmployeeOverviewStore);
    }, [sdk]);

    // Define columns for employees
    const columns: MatenTableColumn[] = [
        {
            field: 'id',
            header: 'Id',
            sortable: true,
        },
        {
            field: 'firstname',
            header: 'Vorname',
            sortable: true,
        },
        {
            field: 'lastname',
            header: 'Nachname',
            sortable: true,
        },
        {
            field: 'phonenumber',
            header: 'Rufnummer',
            sortable: true,
        },
        {
            field: 'solved_training',
            header: 'Schulungen',
            sortable: true,
        },
        {
            field: 'created',
            header: 'Erstellt',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.created),
        },
        {
            field: 'updated',
            header: 'Aktualisiert',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.updated),
        },
    ];

    // Define actions for employees
    const actions: MatenTableAction[] = [
        {
            icon: 'pi pi-pencil',
            tooltip: 'Reinigungskraft bearbeiten',
            tooltipPosition: 'left',
            onClick: (rowData: any) => {
                sdk.openDialog(EmployeeDialogStore, { employee_id: rowData.id });
            },
            onTestId: rowData => `edit-employee-btn-${rowData.id}`,
        },
    ];

    // Additional header content with filters
    const headerContent = (
        <>
            <Button
                label={'Neu'}
                icon="pi pi-plus"
                aria-label="Filter"
                onClick={() => sdk.openDialog(EmployeeDialogStore, {})}
                style={{ marginRight: '0.5rem' }}
                data-testid="employees-button-new"
            />
            <Button
                icon="pi pi-sync"
                aria-label="Aktualisieren"
                onClick={() => sdk.currentStore.refresh()}
                disabled={!!sdk.currentStore?.refreshTimer}
                data-testid="refresh-orders-btn"
            />
        </>
    );

    const tableFilter: MatenFilter[] = [
        {
            title: 'Objektleiter auswählen',
            property: 'filterManager',
            removeFilter: (store: any) => store.applyFilterManager(null),
            chipContent: (filter: any) => `Objektleiter: ${filter?.name || ''}`,
            filterComponent: (store: any) => (
                <Dropdown
                    value={store?.filterManager || null}
                    options={store?.available_managers || []}
                    onChange={(e: any) => store.applyFilterManager(e.value)}
                    onShow={() => store?.fetchAvailableManagers()} // Lazy load managers when dropdown is focused
                    placeholder="Objektleiter auswählen"
                    optionLabel="name"
                    loading={false}
                    style={{ width: '100%' }}
                    data-testid="manager-filter-dropdown"
                />
            ),
        },
        {
            title: 'Schulungsübersicht',
            property: 'filterTrainingLabel',
            removeFilter: (store: any) => {
                store.applyFilterTraining(null); // Clear the manager filter
                store.applyFilterTrainingStatus(null); // Clear the manager filter
            },
            chipContent: (filter: any) => `${filter}`,
            filterComponent: (store: any) => (
                <>
                    <Dropdown
                        value={store?.filterTraining || null}
                        options={store?.available_trainings || []}
                        onChange={(e: any) => store.applyFilterTraining(e.value)}
                        onShow={() => store.fetchAvailableTrainings()}
                        placeholder="Schulung auswählen"
                        optionLabel="name"
                        loading={false}
                        style={{ width: '100%', marginBottom: '0.5rem' }}
                        data-testid="training-filter-dropdown"
                    />
                    <Dropdown
                        value={store?.filterTrainingStatus || 0}
                        options={[
                            { label: 'Bestanden', value: 1 },
                            { label: 'Nicht bestanden', value: 0 },
                        ]}
                        onChange={(e: any) => store.applyFilterTrainingStatus(e.value)}
                        placeholder="Status auswählen"
                        style={{ width: '100%' }}
                        data-testid="training-status-dropdown"
                    />
                </>
            ),
        },
    ];

    return (
        <MatenTable
            data={sdk.currentStore?.list}
            store={sdk.currentStore}
            columns={columns}
            actions={actions}
            filter={tableFilter}
            search={{
                placeholder: 'Reinigungskräfte durchsuchen',
            }}
            emptyMessage="Keine Reinigungskräfte gefunden"
            entity={{ singular: 'Reinigungskraft', plural: 'Reinigungskräfte' }}
            testId={'employees'}
            toolbarContent={headerContent}
            pagination={{ rows: 30 }}
        />
    );
});

export default EmployeeOverview;
